/**
 * Session Realtime Indicator — Brief F Correction §2
 *
 * Persistent, always-visible connection state indicator for live sessions.
 * Combines the session data channel status (_sessionChannelStatus) with the
 * presence channel status (presenceStore.isConnected) to give a single honest
 * signal to participants: are they actually seeing live updates?
 *
 * States:
 *   connected    → green  · "Live"
 *   reconnecting → amber  · "Reconnecting…"
 *   disconnected → red    · "Offline — changes may not sync"
 */

import { useSessionStore } from '../../stores/prioritization/sessionStore';
import { usePresenceStore } from '../../stores/prioritization/presenceStore';

interface SessionRealtimeIndicatorProps {
  sessionId: string;
}

type ConnectionState = 'connected' | 'reconnecting' | 'disconnected';

export function SessionRealtimeIndicator({ sessionId }: SessionRealtimeIndicatorProps) {
  const channelStatus = useSessionStore(
    (s) => s._sessionChannelStatus[sessionId] ?? 'disconnected'
  );
  const presenceConnected = usePresenceStore((s) => s.isConnected);

  // The combined state is the pessimistic view: if either channel is degraded, reflect that.
  const state: ConnectionState = (() => {
    if (channelStatus === 'connected' && presenceConnected) return 'connected';
    if (channelStatus === 'reconnecting') return 'reconnecting';
    if (channelStatus === 'connected' && !presenceConnected) return 'reconnecting';
    return 'disconnected';
  })();

  const config = {
    connected: {
      dot: 'bg-[var(--green)]',
      text: 'text-[var(--green)] dark:text-[var(--text-ter)]',
      label: 'Live',
      title: 'Realtime sync active — changes appear instantly for all participants',
    },
    reconnecting: {
      dot: 'bg-[var(--mustard)] animate-pulse',
      text: 'text-[var(--mustard)]',
      label: 'Reconnecting…',
      title: 'Connection interrupted — attempting to reconnect. Your changes are saving.',
    },
    disconnected: {
      dot: 'bg-[var(--coral)]',
      text: 'text-[var(--coral)]',
      label: 'Offline — changes may not sync',
      title: 'No realtime connection. Changes will save locally but may not appear to others until you reconnect.',
    },
  }[state];

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium ${config.text}`}
      title={config.title}
      aria-label={`Connection status: ${config.label}`}
    >
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${config.dot}`} />
      {config.label}
    </span>
  );
}
